import Moment from "moment";
import Papa from "papaparse";
import Big from "big.js";
import { SaleService } from "core/service";
import { SalePersistence } from "core/persistence";
import RuntimeRepository from "./runtime.repository";
import BaseRepository from "./base.repository";
import UserRepository from "./user.repository";
import CompanyRepository from "./company.repository";
import { Sale } from "core/entity";

class SaleRepository extends BaseRepository {
  syncable = false;

  getEntityIdentifier() {
    return "sale";
  }

  resourceName() {
    return "vendas";
  }

  getPersistenceProvider() {
    return SalePersistence;
  }

  getServiceProvider() {
    return SaleService;
  }

  async modelToEntity(model) {
    const entity = new Sale(model);

    const salesmanResult = await UserRepository.get(entity.salesman_uuid);
    const posOperatorResult = await UserRepository.get(
      entity.pos_operator_uuid
    );
    const companyResult = await CompanyRepository.get(entity.company_uuid);

    if (salesmanResult.success) {
      entity.salesman = salesmanResult.data;
    }

    if (posOperatorResult.success) {
      entity.pos_operator = posOperatorResult.data;
    }

    if (companyResult.success) {
      entity.company = companyResult.data;
    }

    return entity;
  }

  async getSoldProducts(filters) {
    const soldProducts = [];

    const success = await this.mPersistence.stream(filters, sale => {
      sale.cart_items.forEach(cart_item => {
        const uuid = cart_item.sellable.uuid.split(":").splice(-1)[0];

        if (!soldProducts.includes(uuid)) {
          soldProducts.push(uuid);
        }
      });
    });

    return success
      ? { success, fatal: false, data: soldProducts }
      : { success };
  }

  async getReportSummary(filters = [], product_detail = false) {
    const salesPromises = [];

    const cartItemRevenue = cartItem =>
      cartItem.quantity * cartItem.sellable.sell_value - cartItem.discount;
    const cartItemCost = cartItem =>
      cartItem.quantity * (cartItem.sellable.cost_value || 0);

    const success = await this.mPersistence.stream(filters, model => {
      salesPromises.push(this.modelToEntity(model));
    });

    const sales = await Promise.all(salesPromises);
    const summary = {
      count: 0,
      item_count: {},
      revenues: 0,
      costs: 0,
      profit: 0
    };

    sales.forEach(sale => {
      summary.count += 1;
      summary.revenues += sale.revenues();
      summary.costs += sale.costs();

      if (product_detail) {
        sale.cart_items.forEach(cart_item => {
          const uuid = cart_item.sellable.uuid.split(":").splice(-1)[0];

          if (!summary.item_count[uuid]) {
            summary.item_count[uuid] = {
              count: Big(0),
              revenues: 0,
              costs: 0,
              profit: 0,
              discount: 0
            };
          }

          summary.item_count[uuid].count = summary.item_count[uuid].count.add(
            cart_item.quantity
          );
          summary.item_count[uuid].revenues += cartItemRevenue(cart_item);
          summary.item_count[uuid].costs += cartItemCost(cart_item);
          summary.item_count[uuid].discount += cart_item.discount;
        });

        Object.keys(summary.item_count).forEach(uuid => {
          summary.item_count[uuid].profit =
            summary.item_count[uuid].revenues - summary.item_count[uuid].costs;
        });
      }
    });

    summary.profit = summary.revenues - summary.costs;

    return success ? { success, fatal: false, data: summary } : { success };
  }

  async getReportCsvContent({ order, filters }) {
    const listResult = await this.mPersistence.getList({
      order,
      filters,
      pagination: false
    });

    if (!listResult.success) {
      return listResult;
    }

    const header = [
      "Data",
      "Documento",
      "Status",
      "Faturamento",
      "Custos",
      "Lucro Bruto"
    ];
    const data = await Promise.all(
      listResult.data.map(sale => this._saleToReportCsvLine(sale))
    );
    const content = Papa.unparse({ fields: header, data: data });

    return { success: true, data: content };
  }

  async _saleToReportCsvLine(saleObject) {
    const sale = await this.modelToEntity(saleObject);

    return [
      sale.getCreatedAt().format("DD/MM/YYYY HH:mm:ss"),
      sale.receiptName(),
      sale.statusLabel(),
      Big(sale.revenues())
        .div(100)
        .toFixed(2),
      Big(sale.costs())
        .div(100)
        .toFixed(2),
      Big(sale.absoluteProfit())
        .div(100)
        .toFixed(2)
    ];
  }

  async totalRevenues(filters = []) {
    // TODO: Remove company_uuid from fetch
    const currentUser = await this.mRuntime.get("currentUser");
    const companyUuid = currentUser.company_uuid;

    const result = await this.mService.fetch(companyUuid, {
      filters,
      transforms: [{ field: "total_value", operation: "SUM" }]
    });

    if (!result.success) {
      return result;
    }

    return { success: true, data: result.data.result };
  }

  async getDocument(uuid) {
    const currentUser = await this.mRuntime.get(
      RuntimeRepository.props.currentUser
    );
    return await this.mService.getDocument(currentUser.company_uuid, uuid);
  }

  async getCsvContent({ order, filters }) {
    const fields = [
      "Data",
      "Total (R$)",
      "Serie",
      "Numero do Documento",
      "Tipo",
      "Cliente",
      "Meio de Pagamento",
      "Status",
      "Usuário",
      "Vendedor"
    ];
    const data = [];

    // TODO: Remove company_uuid from stream
    const currentUser = await this.mRuntime.get("currentUser");
    const companyUuid = currentUser.company_uuid;

    const success = await this.mService.stream(
      companyUuid,
      { order, filters },
      async model => {
        const sale = await this.modelToEntity(model);
        const documentData = sale.getDocumentData();
        data.push([
          sale.getActivatedAt().format("DD/MM/YYYY HH:mm:ss"),
          Big(sale.total_value)
            .div(100)
            .toFixed(2),
          documentData.serie,
          documentData.number,
          documentData.type,
          sale.getCustomer().identifier("-"),
          sale.paymentMethods(),
          sale.statusLabel(),
          sale.pos_operator ? sale.pos_operator.name || "-" : "-",
          sale.salesman ? sale.salesman.name || "-" : "-"
        ]);
      }
    );

    if (!success) {
      return { success: false };
    }

    return {
      success: true,
      data: Papa.unparse({ fields, data })
    };
  }

  async cancelOrder(sale) {
    const currentUser = await this.runtimeRepository.get(
      RuntimeRepository.props.currentUser
    );
    return await this.saleService.cancelOrder(currentUser.company_uuid, sale);
  }

  async cancel(sale) {
    return await this.mService.cancelNfce(sale.uuid);
  }

  _generateCancellationXml(doc) {
    const info = this._getDocInfo(doc);

    return `
    <EnvioEvento>
      <ModeloDocumento>NFCe</ModeloDocumento>
      <Versao>${info.version}</Versao>
      <Evento>
        <NtfCnpjEmissor>${info.issuer}</NtfCnpjEmissor>
        <NtfNumero>${info.number}</NtfNumero>
        <NtfSerie>${info.serie}</NtfSerie>
        <tpAmb>${info.env}</tpAmb>
        <EveInf>
          <EveDh>${info.datetime}</EveDh>
          <EveFusoHorario>${info.timezone}</EveFusoHorario>
          <EveTp>110111</EveTp>
          <EvenSeq>1</EvenSeq>
          <Evedet>
            <EveDesc>Cancelamento</EveDesc>
            <EvenProt>0</EvenProt>
            <EvexJust>${info.reason}</EvexJust>
          </Evedet>
        </EveInf>
      </Evento>
    </EnvioEvento>
    `;
  }

  _getDocInfo(doc) {
    const now = new Moment();
    const datetime = now.format("YYYY-MM-DDTHH:mm:ss");
    const timezone = now.format("Z");
    const cnpj = doc.access_key.slice(6, 20);

    return {
      version: "3.10",
      issuer: cnpj,
      number: doc.number,
      serie: doc.serie,
      env: doc.environment_type,
      datetime,
      timezone,
      reason: "Venda cancelada"
    };
  }
  async getSaleHourReport(startDate, endDate) {
    return await this.mService.report({
      params: { start_date: startDate, end_date: endDate }
    });
  }
}

export default new SaleRepository();



// WEBPACK FOOTER //
// ./src/core/repository/sale.repository.js